import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { INVOICE_STATUSES } from "constants/statuses";
import { relativeOrDateTime, toDollar } from "utils";

import { useReferrerState } from "hooks";
import { useInternalInvoices, useInternalInvoiceDownload } from "hooks/api";
import { useSnackbar } from "contexts";
import { InfoIcon, DownloadIcon, ReceiptTextIcon } from "lucide-react";
import { ROUTES } from "routes";

import type { GridRowSelectionModel } from "@mui/x-data-grid-pro";

import { Stack } from "@mui/material";
import {
  EmptyState,
  Header,
  Table,
  LinkCell,
  StatusChipCell,
  CategoryCell,
  ActionDropdown,
  UserCell,
  CompanyCell,
  CompanyBlock,
  PaymentCell,
  CompanyFilter,
} from "components";
import { AddressPreview } from "components/display/AddressPreview";
import { statusFilterOptions } from "components/input/filters/StatusChipFilter";

/**
 * VRG-411 — the statuses the invoices endpoint can actually filter by.
 *
 * Rows here come from Stripe (`invoice.status`), and Stripe's invoice list accepts exactly
 * ONE of these five. `INVOICE_STATUSES` additionally carries `failed` and `refunded`, which
 * are not Stripe invoice statuses and therefore can never be a row value on this table — the
 * filter was offering two options that matched nothing, and the API's `IsEnum` would reject
 * either of them outright. Narrowing the options is what makes the selection safe to send.
 */
const STRIPE_LISTABLE_STATUSES = [
  "draft",
  "open",
  "paid",
  "uncollectible",
  "void",
] as const;
type StripeListableStatus = (typeof STRIPE_LISTABLE_STATUSES)[number];

const PAYMENT_STATUS_FILTER_OPTIONS = statusFilterOptions(
  Object.fromEntries(
    STRIPE_LISTABLE_STATUSES.filter((s) => s in INVOICE_STATUSES).map((s) => [
      s,
      (INVOICE_STATUSES as Record<string, any>)[s],
    ])
  )
);

/**
 * Cross-company internal invoices (VERA-882). A faithful mirror of the per-company
 * Billing → Invoices table (apps/platform/.../settings/BillingPage/components/Invoices),
 * with two differences: a Company column pinned to the left, and a server-side Company
 * filter (Stripe's global list can't be filtered by Vera company, so picking one
 * narrows the query to that company's invoices). platform_admin + platform_manager.
 */
export const InternalInvoicesPage = () => {
  const navigate = useNavigate();
  const { open: notify } = useSnackbar();
  // Referrer state handed to the invoice drill-in so its Header offers "← Back to Invoices".
  const referrer = useReferrerState();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [pageCursors, setPageCursors] = useState<
    Record<number, string | undefined>
  >({ 0: undefined });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  // Company filter (server-side, multi-select). `companyIds` is the applied set that
  // drives the query; `companyDraft` is the pending selection edited in the Filters
  // panel, committed only on Apply (matches CertificatesPage's filter pattern). Empty
  // = all companies.
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [companyDraft, setCompanyDraft] = useState<string[]>([]);

  // VRG-411 — payment status, server-side. The list is cursor-paginated server-side, so the
  // drawer's client filter only ever narrowed the page in front of you: filtering to "Open"
  // from page 1 showed the open invoices ON page 1 and nothing else, with no way to tell that
  // apart from "there are no more". The endpoint takes `status` (ListInvoicesQueryDto) and
  // passes it to Stripe on all three of its branches (global / one company / many), so this
  // is a real server filter rather than a param the API ignores.
  const [paymentStatus, setPaymentStatus] = useState<
    StripeListableStatus | undefined
  >(undefined);

  const queryParams = useMemo(
    () => ({
      limit: paginationModel.pageSize,
      startingAfter:
        paginationModel.page === 0
          ? undefined
          : pageCursors[paginationModel.page],
      companyIds: companyIds.length ? companyIds : undefined,
      status: paymentStatus,
    }),
    [paginationModel, pageCursors, companyIds, paymentStatus]
  );

  const { data, isLoading, isRefetching } = useInternalInvoices(queryParams);
  const downloadInvoice = useInternalInvoiceDownload();

  const invoices = data?.invoices ?? [];

  const rows = useMemo(
    () =>
      invoices.map((invoice) => ({
        id: invoice.id,
        number: invoice.number,
        company: {
          id: invoice.companyId,
          name: invoice.companyName ?? "—",
          logo: null,
        },
        created_at: invoice.createdAt,
        total: invoice.total ?? invoice.amountDue ?? 0,
        type: invoice.type,
        orderId: invoice.orderId,
        payment_status: invoice.status ?? "open",
        payment_method: invoice.paymentMethod,
        shipping_address: invoice.shipping,
        customerName: invoice.customerName,
        customerEmail: invoice.customerEmail,
        itemsCount: invoice.itemsCount,
      })),
    [invoices]
  );

  const handleViewInvoice = useCallback(
    (row: { id: string }) => {
      navigate(ROUTES.INTERNAL_INVOICE(row.id), { state: referrer });
    },
    [navigate, referrer]
  );

  const handleDownloadInvoice = useCallback(
    async (row: { id: string }) => {
      try {
        const { url } = await downloadInvoice.mutateAsync(row.id);
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (error) {
        notify({
          type: "error",
          text: "Unable to download invoice right now.",
        });
      }
    },
    [downloadInvoice, notify]
  );

  const getActionItems = useCallback(
    (row: { id: string }) => [
      {
        label: "Details",
        onClick: () => handleViewInvoice(row),
        icon: <InfoIcon strokeWidth={1} />,
      },
      {
        label: "Download",
        onClick: () => handleDownloadInvoice(row),
        icon: <DownloadIcon strokeWidth={1} />,
      },
    ],
    [handleViewInvoice, handleDownloadInvoice]
  );

  const handleBulkDownload = useCallback(
    async (selectedRows: Array<{ id: string }>) => {
      try {
        // Stripe issues one signed URL per invoice; download each sequentially.
        for (const row of selectedRows) {
          const { url } = await downloadInvoice.mutateAsync(row.id);
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch (error) {
        notify({ type: "error", text: "Unable to download invoices right now." });
      }
    },
    [downloadInvoice, notify]
  );

  const getBulkActions = useCallback(
    (selectedRows: Array<{ id: string }>) => [
      {
        label: "Download",
        onClick: () => handleBulkDownload(selectedRows),
        icon: <DownloadIcon strokeWidth={1} />,
      },
    ],
    [handleBulkDownload]
  );

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Invoice ID",
        width: 190,
        pinned: "left",
        sortable: false,
        disableReorder: true,
        type: "string",
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }: { row: { id: string; number: string | null } }) => (
          <LinkCell
            align="left"
            link={`#${row.number ?? row.id}`}
            route={ROUTES.INTERNAL_INVOICE(row.id)}
          />
        ),
      },
      {
        field: "company",
        headerName: "Company",
        width: 260,
        sortable: false,
        disableReorder: true,
        // The Company filter is server-side (Stripe's global list can't be filtered
        // by Vera company client-side), provided via the Filters drawer's Company
        // selector below — so suppress the auto per-column (client-side) filter that
        // would otherwise only filter the loaded page.
        filterable: false,
        type: "string",
        renderCell: ({ row }: { row: { company: { id: string | null; name: string } } }) =>
          row.company?.id ? (
            <CompanyCell company={row.company} />
          ) : (
            row.company?.name ?? "—"
          ),
      },
      {
        field: "created_at",
        headerName: "Date",
        type: "string",
        width: 200,
        renderCell: ({ value }: { value: string | null | undefined }) =>
          value ? relativeOrDateTime(value) : "—",
      },
      {
        field: "itemsCount",
        headerName: "Products",
        type: "number",
        width: 120,
        // "Products" = the goods on an order invoice. A subscription invoice bills the
        // plan + metered usage (cert/AI-query usage rides on the same monthly
        // subscription invoice as a usage line item — not products), so leave this
        // blank for subscription/other invoices.
        renderCell: ({
          row,
        }: {
          row: { type: string; itemsCount: number | null };
        }) =>
          row.type === "order" && row.itemsCount != null ? row.itemsCount : "—",
      },
      {
        field: "total",
        headerName: "Total",
        type: "number",
        width: 120,
        valueFormatter: (value: number) => toDollar(value ?? 0, true),
      },
      {
        field: "type",
        headerName: "Type",
        type: "multiSelect",
        width: 160,
        renderCell: ({ row }: { row: { type: string } }) => (
          <CategoryCell
            category={row.type ?? "other"}
            filter
            align="start"
            bordered={false}
          />
        ),
      },
      {
        field: "placed_by",
        headerName: "Placed by",
        type: "string",
        sortable: false,
        width: 240,
        // A tag *order* is placed by a person; a *subscription* charge is an
        // org-level recurring obligation placed by the Company (no individual
        // "places" it each cycle). Switch the renderer on invoice type.
        renderCell: ({
          row,
        }: {
          row: {
            type: string;
            customerName: string | null;
            customerEmail: string | null;
            company: any;
          };
        }) =>
          row.type === "order" ? (
            <UserCell
              user={{
                name: row.customerName,
                email: row.customerEmail,
              }}
            />
          ) : (
            <Stack sx={{ py: 2 }} justifyContent="center" height="100%">
              <CompanyBlock company={row.company} />
            </Stack>
          ),
      },
      {
        field: "order",
        headerName: "Order",
        type: "string",
        width: 180,
        sortable: false,
        // Order-type invoices link out to their order; subscription invoices
        // have no order (renders "—").
        renderCell: ({ row }: { row: { orderId: string | null } }) =>
          row.orderId ? (
            <LinkCell
              link={`#${String(row.orderId).slice(0, 8)}`}
              route={ROUTES.TAGS_ORDER(row.orderId)}
            />
          ) : (
            "—"
          ),
      },
      {
        field: "payment_status",
        headerName: "Payment status",
        type: "statusChip",
        // The five Stripe-listable statuses only — see STRIPE_LISTABLE_STATUSES. The CELL
        // still renders from the full INVOICE_STATUSES map, so nothing loses its colour.
        options: PAYMENT_STATUS_FILTER_OPTIONS,
        width: 150,
        renderCell: ({ value }: { value: string }) => (
          <StatusChipCell status={value ?? "open"} statuses={INVOICE_STATUSES} />
        ),
      },
      {
        field: "payment_method",
        headerName: "Payment method",
        type: "string",
        width: 240,
        renderCell: ({ row }: { row: { payment_method: any } }) =>
          row.payment_method ? (
            <PaymentCell paymentMethod={row.payment_method} link={false} />
          ) : (
            "—"
          ),
      },
      {
        field: "shipping_address",
        headerName: "Shipping address",
        type: "string",
        width: 300,
        renderCell: ({ row }: { row: { shipping_address: any } }) =>
          row.shipping_address ? (
            <AddressPreview address={row.shipping_address} />
          ) : (
            "—"
          ),
      },
      {
        field: "actions",
        headerName: false,
        pinned: "right",
        sortable: false,
        disableReorder: true,
        align: "center",
        width: 54,
        renderCell: ({ row }: { row: { id: string } }) => (
          <ActionDropdown items={getActionItems(row)} />
        ),
      },
    ],
    [getActionItems]
  );

  useEffect(() => {
    if (!data?.nextCursor) {
      return;
    }

    setPageCursors((prev) => {
      if (prev[paginationModel.page + 1] === data.nextCursor) {
        return prev;
      }

      return {
        ...prev,
        [paginationModel.page + 1]: data.nextCursor ?? undefined,
      };
    });
  }, [data?.nextCursor, paginationModel.page]);

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      const pageSizeChanged = model.pageSize !== paginationModel.pageSize;
      if (pageSizeChanged) {
        setPageCursors({ 0: undefined });
        setPaginationModel({ page: 0, pageSize: model.pageSize });
        return;
      }

      if (model.page > paginationModel.page) {
        if (!data?.hasMore) {
          return;
        }
        const cursorForNextPage =
          pageCursors[model.page] ?? data?.nextCursor ?? null;
        if (!cursorForNextPage) {
          return;
        }
        if (!pageCursors[model.page]) {
          setPageCursors((prev) => ({
            ...prev,
            [model.page]: cursorForNextPage,
          }));
        }
        setPaginationModel(model);
        return;
      }

      if (model.page < paginationModel.page) {
        setPaginationModel(model);
      }
    },
    [data?.hasMore, data?.nextCursor, pageCursors, paginationModel]
  );

  // Applying the Company filter is a new query — reset cursor pagination to page 0.
  const applyCompanyFilter = useCallback(() => {
    setCompanyIds(companyDraft);
    setPageCursors({ 0: undefined });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [companyDraft]);

  /**
   * VRG-411 — turn the drawer's applied set into the server's `status` param.
   *
   * Stripe's invoice list accepts ONE status, while the statusChip field is multi-select, so
   * a server filter is only expressible for a single selection. With two or more ticked we
   * send nothing and let the grid narrow the loaded page, which is exactly the behaviour that
   * exists today — strictly better than sending one of them and reporting the others as
   * having no invoices at all.
   */
  const handleFiltersChange = useCallback(
    (applied: { field: string; operator?: string; value: unknown }[]) => {
      const raw = applied.find((f) => f.field === "payment_status")?.value;
      const values = (
        Array.isArray(raw) ? raw : raw == null ? [] : [raw]
      ) as string[];
      const next =
        values.length === 1 &&
        (STRIPE_LISTABLE_STATUSES as readonly string[]).includes(values[0])
          ? (values[0] as StripeListableStatus)
          : undefined;
      // The drawer re-reports the whole applied set on every commit; bail when this page's
      // slice of it is unchanged, or an unrelated filter would reset cursor pagination.
      if (next === paymentStatus) {
        return;
      }
      setPaymentStatus(next);
      // A different query invalidates every cursor collected for the old one.
      setPageCursors({ 0: undefined });
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [paymentStatus]
  );

  // Draft differs from applied when the selected-company sets differ (order-agnostic).
  const companyFilterDirty =
    companyDraft.length !== companyIds.length ||
    companyDraft.some((id) => !companyIds.includes(id));

  const estimatedRowCount =
    paginationModel.page * paginationModel.pageSize +
    rows.length +
    (data?.hasMore ? paginationModel.pageSize : 0);

  return (
    <>
      <Header title="Invoices" />
      <Table
        fillBottom
        loading={isLoading || isRefetching}
        columns={columns}
        rows={rows}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={estimatedRowCount}
        bulkActions={getBulkActions}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        onFiltersChange={handleFiltersChange}
        extraFilters={
          <CompanyFilter value={companyDraft} onChange={setCompanyDraft} />
        }
        extraFiltersDirty={companyFilterDirty}
        onFiltersApply={applyCompanyFilter}
        // Clear owns the extras too — reset the draft AND the applied set, or the
        // next commit re-applies the companies the user just cleared.
        onClearExtraFilters={() => {
          setCompanyDraft([]);
          setCompanyIds([]);
          setPageCursors({ 0: undefined });
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        emptyState={
          <EmptyState
            title="No invoices yet"
            text="Invoices across all companies will appear here."
            icon={<ReceiptTextIcon strokeWidth={1} size={32} />}
          />
        }
      />
    </>
  );
};
